import * as yup from 'yup';
import { CUSTOMER_COMMAND_FIELDS, toDateInputValue, getTodayDateInputValue } from '../models/Client';

const NIP_DIGITS_MESSAGE = 'NIP musi zawierać 10 cyfr';
const POSTAL_CODE_MESSAGE = 'Kod pocztowy musi być w formacie 00-000';
const EMAIL_MESSAGE = 'Podaj poprawny adres e-mail';
export const ADDRESS_ALREADY_EXISTS_MESSAGE =
  'Klient z tym adresem już istnieje w systemie.';

const ADDRESS_ALREADY_EXISTS_PATTERN =
  /CustomerAddressAlreadyExists|address.*already exists|already exists.*address|existsByAddress/i;

const collectResponseTexts = (data) => {
  if (!data) return [];
  if (typeof data === 'string') return [data];

  return [data.message, data.error, data.detail, data.title].filter(
    (value) => typeof value === 'string' && value.trim()
  );
};

const isAddressAlreadyExistsError = (data, status) => {
  const texts = collectResponseTexts(data);

  if (texts.some((text) => ADDRESS_ALREADY_EXISTS_PATTERN.test(text))) {
    return true;
  }

  if (typeof data?.address === 'string' && ADDRESS_ALREADY_EXISTS_PATTERN.test(data.address)) {
    return true;
  }

  return status === 409 && texts.some((text) => /address|adres|CustomerAddress/i.test(text));
};

export const normalizeNip = (nip) => nip.replace(/\D/g, '');

const sharedFieldRules = {
  companyName: yup
    .string()
    .trim()
    .required('Nazwa firmy jest wymagana'),
  nip: yup
    .string()
    .trim()
    .test('nip-digits', NIP_DIGITS_MESSAGE, (value) => {
      if (!value) return true;
      return normalizeNip(value).length === 10;
    }),
  postalCode: yup
    .string()
    .trim()
    .required('Kod pocztowy jest wymagany')
    .matches(/^\d{2}-\d{3}$/, POSTAL_CODE_MESSAGE),
  city: yup.string().trim().required('Miasto jest wymagane'),
  address: yup.string().trim().required('Ulica i numer są wymagane'),
  phone: yup.string().trim().required('Numer telefonu jest wymagany'),
  email: yup
    .string()
    .trim()
    .test('email', EMAIL_MESSAGE, (value) => {
      if (!value) return true;
      return yup.string().email().isValidSync(value);
    }),
  contactPerson: yup.string().trim(),
  status: yup.string().required(),
  priority: yup.string().required(),
  notes: yup.string().trim(),
  lastContactDate: yup
    .string()
    .nullable()
    .test(
      'last-contact-not-future',
      'Data ostatniego kontaktu nie może być w przyszłości',
      (value) => {
        if (!value) return true;
        const selected = toComparableDate(value);
        const today = getTodayDateInputValue();
        return !selected || selected <= today;
      }
    ),
};

export const UPDATE_FORM_FIELDS = Object.keys(sharedFieldRules);

export const customerFormSchema = yup.object({
  ...sharedFieldRules,
});

export const customerUpdateFormSchema = yup.object(sharedFieldRules);

const collectValidationErrors = (err) => {
  if (!err.inner) {
    return { isValid: false, fieldErrors: {}, generalError: err.message };
  }

  const fieldErrors = err.inner.reduce((acc, error) => {
    if (error.path) {
      acc[error.path] = error.message;
    }
    return acc;
  }, {});

  return { isValid: false, fieldErrors };
};

export const validateCustomerForm = async (form, { mode = 'create', fields = null } = {}) => {
  const schema = mode === 'edit' ? customerUpdateFormSchema : customerFormSchema;
  const fieldsToValidate = fields ?? Object.keys(schema.fields);

  if (mode === 'edit' && fieldsToValidate.length === 0) {
    return {
      isValid: false,
      fieldErrors: {},
      generalError: 'Brak zmian do zapisania.',
    };
  }

  try {
    await schema.pick(fieldsToValidate).validate(form, { abortEarly: false });
    return { isValid: true, fieldErrors: {} };
  } catch (err) {
    return collectValidationErrors(err);
  }
};

export const parseCustomerApiErrors = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status;

  if (isAddressAlreadyExistsError(data, status)) {
    return {
      fieldErrors: { address: ADDRESS_ALREADY_EXISTS_MESSAGE },
      generalError: null,
    };
  }

  if (!data) {
    return {
      fieldErrors: {},
      generalError: error?.message || 'Nie udało się zapisać klienta.',
    };
  }

  if (typeof data === 'string') {
    const generalError = ADDRESS_ALREADY_EXISTS_PATTERN.test(data)
      ? ADDRESS_ALREADY_EXISTS_MESSAGE
      : data;
    return {
      fieldErrors: ADDRESS_ALREADY_EXISTS_PATTERN.test(data)
        ? { address: ADDRESS_ALREADY_EXISTS_MESSAGE }
        : {},
      generalError: ADDRESS_ALREADY_EXISTS_PATTERN.test(data) ? null : generalError,
    };
  }

  if (data.message && typeof data.message === 'string' && Object.keys(data).length === 1) {
    const message = translateApiFieldMessage(data.message);
    return {
      fieldErrors: message === ADDRESS_ALREADY_EXISTS_MESSAGE ? { address: message } : {},
      generalError: message === ADDRESS_ALREADY_EXISTS_MESSAGE ? null : message,
    };
  }

  const fieldErrors = {};
  let generalError = null;

  Object.entries(data).forEach(([key, value]) => {
    if (key === 'message' || key === 'error') {
      if (typeof value === 'string') generalError = value;
      return;
    }
    if (typeof value === 'string') {
      fieldErrors[key] = value;
    }
  });

  const translatedFieldErrors = Object.fromEntries(
    Object.entries(fieldErrors).map(([key, value]) => [key, translateApiFieldMessage(value)])
  );

  const translatedGeneralError = generalError ? translateApiFieldMessage(generalError) : null;

  if (translatedFieldErrors.address === ADDRESS_ALREADY_EXISTS_MESSAGE) {
    return { fieldErrors: translatedFieldErrors, generalError: null };
  }

  if (translatedGeneralError === ADDRESS_ALREADY_EXISTS_MESSAGE) {
    return {
      fieldErrors: { ...translatedFieldErrors, address: ADDRESS_ALREADY_EXISTS_MESSAGE },
      generalError: null,
    };
  }

  return {
    fieldErrors: translatedFieldErrors,
    generalError:
      translatedGeneralError
      || (Object.keys(translatedFieldErrors).length ? null : error?.message),
  };
};

export const translateApiFieldMessage = (message) => {
  if (!message) return message;
  if (ADDRESS_ALREADY_EXISTS_PATTERN.test(message)) {
    return ADDRESS_ALREADY_EXISTS_MESSAGE;
  }
  if (message === 'NIP must contain 10 digits') {
    return NIP_DIGITS_MESSAGE;
  }
  if (/past|przysz/i.test(message)) {
    return 'Data ostatniego kontaktu nie może być w przyszłości';
  }
  return message;
};

const toComparableDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return toDateInputValue(value) || null;
};

const CLEARABLE_OPTIONAL_FIELDS = new Set(['nip', 'email', 'contactPerson', 'notes']);

const normalizeFormValue = (field, value) => {
  if (field === 'nip') {
    const nip = normalizeNip(value);
    if (nip) return nip;
    return CLEARABLE_OPTIONAL_FIELDS.has(field) ? null : undefined;
  }
  if (field === 'lastContactDate') {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T00:00:00`;
    }
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) return trimmed;
    return CLEARABLE_OPTIONAL_FIELDS.has(field) ? null : undefined;
  }
  return value ?? undefined;
};

const valuesEqual = (field, current, original) => {
  if (field === 'lastContactDate') {
    return toComparableDate(current) === toComparableDate(original);
  }
  if (CLEARABLE_OPTIONAL_FIELDS.has(field)) {
    const isEmpty = (value) => value === null || value === undefined;
    if (isEmpty(current) && isEmpty(original)) return true;
  }
  return current === original;
};

export const getChangedUpdateFields = (form, originalForm) => {
  const changedFields = {};

  CUSTOMER_COMMAND_FIELDS.forEach((field) => {
    const current = normalizeFormValue(field, form[field]);
    const original = normalizeFormValue(field, originalForm[field]);

    if (!valuesEqual(field, current, original)) {
      changedFields[field] = current;
    }
  });

  if (Object.prototype.hasOwnProperty.call(form, 'lastContactDate')
    || Object.prototype.hasOwnProperty.call(originalForm, 'lastContactDate')) {
    const current = normalizeFormValue('lastContactDate', form.lastContactDate);
    const original = normalizeFormValue('lastContactDate', originalForm.lastContactDate);

    if (!valuesEqual('lastContactDate', current, original)) {
      changedFields.lastContactDate = current;
    }
  }

  return changedFields;
};

export const buildFieldUpdatePayload = (field, value, originalForm) =>
  getChangedUpdateFields({ ...originalForm, [field]: value }, originalForm);
